import type { ForwardedRef, RefObject } from "react";
import { PureComponent } from "react";

import { defaultAttributes } from "@Components/Component";
import { CloseIconSlimButtonClassName } from "@Components/Icons/CloseIconSlim";
import { SearchIcon, SearchIconV2 } from "@Components/Icons/SearchIcon";
import { getAssetsImage, onDefaultKeyboardUp, requestTrack } from "@Components/Modules/Helpers";

import EyeInteractive from "@Assets/EyeInteractive";

import type { IInput } from "../Input";
import { TextboxAppearance, TextboxType, defaultFalse } from "./TextboxTypes";

import * as css from "./Textbox.module.css";

const getTextboxAppearanceClassname = (appearance?: TextboxAppearance) => {
    switch (appearance) {
        case TextboxAppearance.Search:
            return css.search;
        case TextboxAppearance.SearchWhite:
            return css.searchWhite;
        case TextboxAppearance.SearchGray:
            return css.searchGray;
        case TextboxAppearance.SearchProducts:
            return css.searchProducts;
        case TextboxAppearance.Round:
            return css.round;
        case TextboxAppearance.SearchGlobal:
            return css.searchGlobal;
        case TextboxAppearance.PrefixNumber:
            return css.prefixNumber;
        default:
            return css.standard;
    }
};

export interface ITextbox extends IInput {
    textboxType?: TextboxType;
    passwordToggle?: boolean;
    lockToggle?: boolean;
    appearance?: TextboxAppearance;
    value?: string;
    watermarkText?: string;
    title?: string;
    passwordToggleTitle?: string;
    maxLength?: number;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => boolean | void;
    onFocus?: (event: React.FocusEvent | React.MouseEvent | React.TouchEvent) => void;
    onBlur?: React.FocusEventHandler<HTMLInputElement>;
    isReadOnly?: boolean;
    isTextValid?: boolean;
    disableAutoComplete?: boolean;
    preventPaste?: boolean;
    forwardRef?: RefObject<HTMLInputElement> | ForwardedRef<HTMLInputElement>;
    size?: number;
    minLength?: number;
    cleanTrackerUrl?: string;
    disabled?: boolean;
    dataTestId?: string;
}

interface ITextboxState {
    passwordVisible: boolean;
}

export class Textbox extends PureComponent<ITextbox, ITextboxState> {
    public static defaultProps: ITextbox = {
        textboxType: TextboxType.Text,
        passwordToggle: false,
        lockToggle: false,
        appearance: TextboxAppearance.Standard,
        isReadOnly: false,
        disableAutoComplete: false,
        maxLength: 0,
    };

    public state = {
        passwordVisible: false,
    };

    private renderIcon = () => {
        switch (this.props.appearance) {
            case TextboxAppearance.Search:
            case TextboxAppearance.SearchWhite:
            case TextboxAppearance.SearchProducts:
            case TextboxAppearance.SearchGray:
                return (
                    <span className={css.icon}>
                        <SearchIcon />
                    </span>
                );

            case TextboxAppearance.SearchGlobal:
                return (
                    <button
                        className={css.icon}
                        onClick={this.props.onFocus}
                        onTouchStart={this.props.onFocus}
                        type="button"
                    >
                        <SearchIconV2 />
                    </button>
                );
            case TextboxAppearance.PrefixNumber:
                return <span className={css.icon}>+48</span>;

            default:
                return null;
        }
    };

    private renderClose = () => {
        if (
            this.props.appearance === TextboxAppearance.SearchGlobal &&
            typeof this.props.forwardRef === "object" &&
            this.props.forwardRef?.current?.value &&
            this.props.forwardRef?.current?.value !== ""
        ) {
            return (
                <CloseIconSlimButtonClassName
                    color="#14284f"
                    onClick={(ev) => {
                        this.cleanText(ev);
                        requestTrack(this.props.cleanTrackerUrl);
                    }}
                    className={`${css.closeIcon} ${getTextboxAppearanceClassname(5)}`}
                />
            );
        }
        return null;
    };

    private cleanText = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (typeof this.props.forwardRef === "object" && this.props.forwardRef?.current?.value) {
            this.props.forwardRef.current.value = "";
            this.props.forwardRef.current.focus();

            if (this.props.onChange) {
                this.props.onChange(event as unknown as React.ChangeEvent<HTMLInputElement>);
            }
        }
    };

    private renderPasswordToggle = () => {
        if (this.props.textboxType !== TextboxType.Password || this.props.passwordToggle === false) {
            return null;
        }

        return (
            <button
                aria-label={this.props.passwordToggleTitle}
                title={this.props.passwordToggleTitle}
                className={css.passwordToggle}
                onMouseDown={this.showPassword}
                onMouseUp={this.hidePassword}
                onTouchStart={this.showPassword}
                onTouchEnd={this.hidePassword}
                onKeyUp={onDefaultKeyboardUp}
                tabIndex={0}
                type="button"
            >
                <EyeInteractive isVisible={this.state.passwordVisible} color="#14284f" />
            </button>
        );
    };

    private showPassword = () => this.setState({ passwordVisible: true });

    private hidePassword = () => this.setState({ passwordVisible: false });

    private renderLockToggle = () =>
        this.props.textboxType !== TextboxType.Text || this.props.lockToggle === false ? null : (
            <img
                className={css.lockToggle}
                src={getAssetsImage(process.env.LockIcon)}
                alt="lock"
                aria-label="lock"
            />
        );

    public render() {
        let type = "text";
        if (this.props.textboxType) {
            type = TextboxType[this.props.textboxType].toLowerCase();
        }

        if (this.props.textboxType === TextboxType.Password && this.state.passwordVisible === true) {
            type = "text";
        }

        if (this.props.textboxType === TextboxType.Telephone) {
            type = "tel";
        }

        return (
            <div
                {...defaultAttributes(
                    this.props,
                    css.textboxComponent,
                    getTextboxAppearanceClassname(this.props.appearance),
                )}
            >
                {this.renderIcon()}
                <input
                    ref={this.props.forwardRef}
                    id={this.props.clientId ? `${this.props.clientId}-input` : undefined}
                    type={type}
                    disabled={this.props.disabled}
                    title={this.props.title}
                    value={this.props.value}
                    placeholder={this.props.watermarkText}
                    onChange={this.props.onChange}
                    onKeyDown={this.props.onKeyDown}
                    onFocus={this.props.onFocus}
                    onBlur={this.props.onBlur}
                    size={this.props.size ? this.props.size : undefined}
                    minLength={this.props.minLength ? this.props.minLength : undefined}
                    maxLength={this.props.maxLength ? this.props.maxLength : undefined}
                    onPaste={this.props.preventPaste ? defaultFalse : undefined}
                    readOnly={this.props.isReadOnly}
                    autoComplete={
                        this.props.disableAutoComplete !== undefined &&
                        this.props.disableAutoComplete !== null
                            ? `${this.props.disableAutoComplete}`
                            : undefined
                    }
                    data-testid={this.props.dataTestId}
                    aria-label={this.props["aria-label"]}
                />
                {this.renderPasswordToggle()}
                {this.props.isTextValid && <span className={css.checkIcon} />}
                {this.renderLockToggle()}
                {this.renderClose()}
            </div>
        );
    }
}
